(() => {
  let activeModal = null;
  let previousFocus = null;

  function syncBodyLock() {
    const menuOpen = document.querySelector("[data-mobile-menu].is-open");
    document.body.style.overflow = menuOpen || activeModal ? "hidden" : "";
  }

  function closeModal() {
    if (!activeModal) return;
    activeModal.hidden = true;
    activeModal = null;
    syncBodyLock();
    previousFocus?.focus();
    previousFocus = null;
  }

  function initializeHeader(header) {
    if (header.dataset.initialized === "true") return;
    header.dataset.initialized = "true";

    const section = header.closest(".shopify-section") || document;
    const menuButton = header.querySelector("[data-menu-toggle]");
    const mobileMenu = section.querySelector("[data-mobile-menu]");
    const openLabel = menuButton?.getAttribute("aria-label") || "Open menu";
    const closeLabel = menuButton?.dataset.closeLabel || "Close menu";

    function closeMenu() {
      if (!menuButton || !mobileMenu) return;
      mobileMenu.classList.remove("is-open");
      menuButton.setAttribute("aria-expanded", "false");
      menuButton.setAttribute("aria-label", openLabel);
      syncBodyLock();
    }

    menuButton?.addEventListener("click", () => {
      if (!mobileMenu) return;
      const willOpen = !mobileMenu.classList.contains("is-open");
      mobileMenu.classList.toggle("is-open", willOpen);
      menuButton.setAttribute("aria-expanded", String(willOpen));
      menuButton.setAttribute("aria-label", willOpen ? closeLabel : openLabel);
      syncBodyLock();
    });

    mobileMenu?.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", closeMenu);
    });
  }

  function initializeModalOpener(button) {
    if (button.dataset.initialized === "true") return;
    button.dataset.initialized = "true";
    button.addEventListener("click", () => {
      const name = button.dataset.modelOpen;
      const modal = document.querySelector(`[data-model-modal="${name}"]`);
      if (!modal) return;
      previousFocus = button;
      activeModal = modal;
      modal.hidden = false;
      resetGallery(modal);
      syncBodyLock();
      modal.querySelector(".modal-close")?.focus();
    });
  }

  function initializeModal(modal) {
    if (modal.dataset.initialized === "true") return;
    modal.dataset.initialized = "true";
    modal.querySelectorAll("[data-model-close]").forEach((control) => {
      control.addEventListener("click", closeModal);
    });
  }

  function updateGallery(gallery) {
    const track = gallery.querySelector("[data-gallery-track]");
    const slides = Array.from(gallery.querySelectorAll("[data-gallery-slide]"));
    if (!track || slides.length === 0) return;

    const slideWidth = track.clientWidth || 1;
    const currentIndex = Math.min(
      slides.length - 1,
      Math.max(0, Math.round(track.scrollLeft / slideWidth))
    );
    const current = gallery.querySelector("[data-gallery-current]");
    const previous = gallery.querySelector("[data-gallery-previous]");
    const next = gallery.querySelector("[data-gallery-next]");

    if (current) current.textContent = String(currentIndex + 1);
    if (previous) previous.disabled = currentIndex === 0;
    if (next) next.disabled = currentIndex === slides.length - 1;
    gallery.dataset.galleryIndex = String(currentIndex);
  }

  function moveGallery(gallery, direction) {
    const track = gallery.querySelector("[data-gallery-track]");
    const slides = Array.from(gallery.querySelectorAll("[data-gallery-slide]"));
    if (!track || slides.length < 2) return;

    const currentIndex = Number(gallery.dataset.galleryIndex || 0);
    const targetIndex = Math.min(
      slides.length - 1,
      Math.max(0, currentIndex + direction)
    );
    track.scrollTo({
      left: slides[targetIndex].offsetLeft,
      behavior: "smooth"
    });
  }

  function resetGallery(modal) {
    modal.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      const track = gallery.querySelector("[data-gallery-track]");
      if (!track) return;
      track.scrollLeft = 0;
      gallery.dataset.galleryIndex = "0";
      window.requestAnimationFrame(() => updateGallery(gallery));
    });
  }

  function initializeGallery(gallery) {
    if (gallery.dataset.initialized === "true") return;
    gallery.dataset.initialized = "true";

    const track = gallery.querySelector("[data-gallery-track]");
    const previous = gallery.querySelector("[data-gallery-previous]");
    const next = gallery.querySelector("[data-gallery-next]");
    if (!track) return;

    let scrollFrame = null;
    let draggedPointer = null;
    let dragStartX = 0;
    let dragStartScroll = 0;

    function isModelViewerInteraction(event) {
      return event.composedPath().some((element) => element?.localName === "model-viewer");
    }

    track.addEventListener("scroll", () => {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        updateGallery(gallery);
        scrollFrame = null;
      });
    }, { passive: true });

    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveGallery(gallery, -1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        moveGallery(gallery, 1);
      }
    });

    track.addEventListener("wheel", (event) => {
      if (isModelViewerInteraction(event)) return;
      const canScroll = track.scrollWidth > track.clientWidth;
      const isDesktopPointer = window.matchMedia("(pointer: fine)").matches;
      const isVerticalWheel = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!canScroll || !isDesktopPointer || !isVerticalWheel) return;
      event.preventDefault();
      track.scrollLeft += event.deltaY;
    }, { passive: false });

    track.addEventListener("pointerdown", (event) => {
      if (isModelViewerInteraction(event)) return;
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      draggedPointer = event.pointerId;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add("is-dragging");
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener("pointermove", (event) => {
      if (event.pointerId !== draggedPointer) return;
      track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
    });

    function finishGalleryDrag(event) {
      if (event.pointerId !== draggedPointer) return;
      draggedPointer = null;
      track.classList.remove("is-dragging");
      const targetIndex = Math.round(track.scrollLeft / (track.clientWidth || 1));
      const slides = Array.from(gallery.querySelectorAll("[data-gallery-slide]"));
      const target = slides[Math.min(slides.length - 1, Math.max(0, targetIndex))];
      if (target) {
        track.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
      }
    }

    track.addEventListener("pointerup", finishGalleryDrag);
    track.addEventListener("pointercancel", finishGalleryDrag);

    previous?.addEventListener("click", () => moveGallery(gallery, -1));
    next?.addEventListener("click", () => moveGallery(gallery, 1));

    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(() => updateGallery(gallery));
      observer.observe(track);
    }

    updateGallery(gallery);
  }

  function initializeNewsletter(form) {
    if (form.dataset.initialized === "true") return;
    form.dataset.initialized = "true";
    form.addEventListener("submit", () => {
      const button = form.querySelector("[data-newsletter-submit]");
      if (!button) return;
      const sendingLabel = button.dataset.sendingLabel || "Joining…";
      button.disabled = true;
      button.firstChild.textContent = `${sendingLabel} `;
    });
  }

  function initializeProduct(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const form = root.querySelector(".product-form");
    const variantSelect = root.querySelector("[data-variant-select]");
    const submitButton = root.querySelector("[data-product-submit]");
    const mobileSubmit = root.querySelector("[data-product-submit-trigger]");
    const price = root.querySelector("[data-product-price]");
    const mobilePrice = root.querySelector("[data-mobile-product-price]");
    const sellingPlanInput = root.querySelector("[data-selling-plan-input]");
    const gallery = root.querySelector("[data-product-gallery]");

    function updateVariant() {
      if (!variantSelect) return;
      const option = variantSelect.selectedOptions[0];
      if (!option) return;

      const isAvailable = option.dataset.available === "true";
      const formattedPrice = option.dataset.price || "";
      const mediaId = option.dataset.mediaId;

      if (price && formattedPrice) price.textContent = formattedPrice;
      if (mobilePrice && formattedPrice) mobilePrice.textContent = formattedPrice;

      if (submitButton) {
        submitButton.disabled = !isAvailable;
        const label = isAvailable
          ? submitButton.dataset.availableLabel
          : submitButton.dataset.soldOutLabel;
        submitButton.firstChild.textContent = `${label || ""} `;
      }

      if (mobileSubmit) {
        mobileSubmit.disabled = !isAvailable;
        mobileSubmit.textContent = isAvailable
          ? mobileSubmit.dataset.availableLabel || mobileSubmit.textContent
          : submitButton?.dataset.soldOutLabel || mobileSubmit.textContent;
      }

      if (sellingPlanInput && option.dataset.sellingPlan) {
        sellingPlanInput.value = option.dataset.sellingPlan;
      }

      if (gallery && mediaId) {
        const slide = gallery.querySelector(`[data-media-id="${mediaId}"]`);
        const track = gallery.querySelector("[data-gallery-track]");
        if (slide && track) {
          track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        }
      }

      if (window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", option.value);
        window.history.replaceState({}, "", url);
      }
    }

    variantSelect?.addEventListener("change", updateVariant);

    if (mobileSubmit) {
      mobileSubmit.dataset.availableLabel = mobileSubmit.textContent.trim();
      mobileSubmit.addEventListener("click", () => {
        form?.requestSubmit();
      });
    }
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll?.("[data-chiaja-header]").forEach(initializeHeader);
    scope.querySelectorAll?.("[data-model-open]").forEach(initializeModalOpener);
    scope.querySelectorAll?.("[data-model-modal]").forEach(initializeModal);
    scope.querySelectorAll?.("[data-product-gallery]").forEach(initializeGallery);
    scope.querySelectorAll?.("[data-product-root]").forEach(initializeProduct);
    scope.querySelectorAll?.(".early-access-form").forEach(initializeNewsletter);
  }

  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll("[data-mobile-menu].is-open").forEach((menu) => {
      menu.classList.remove("is-open");
    });
    document.querySelectorAll("[data-menu-toggle]").forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });
    closeModal();
    syncBodyLock();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });
})();
